/**
 * useInvoiceGeneration Hook
 * Custom hook for invoice generation with TanStack Query-like behavior
 */

import { useState } from 'react';
import { invoiceApi } from '@/lib/api/invoice';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { useHistoryStore } from '@/stores/historyStore';
import { getDefaultLogo } from '@/utils/logoStorage';
import {
  getDefaultSignStamp,
} from '@/utils/signStampStorage';
import { logger } from '@/utils/logger';
import type { RateLimitData } from '@/lib/api/types';
import type { Invoice } from '@shared/schemas/invoice.schema';

export function useInvoiceGeneration() {
  const {
    setCurrentInvoice,
    setSessionId,
    setGenerating,
    setError,
    sessionId,
  } = useInvoiceStore();
  const { addToHistory } = useHistoryStore();

  const [rateLimitData, setRateLimitData] = useState<RateLimitData | null>(null);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);

  const generateInvoice = async (text: string) => {
    try {
      setGenerating(true);
      setError(null);
      setRateLimitData(null);
      setShowRateLimitModal(false);

      const response = await invoiceApi.generateInvoice(text, sessionId || undefined);

      if (!response.success || !response.invoice) {
        const error = new Error(response.error || 'Failed to generate invoice') as any;
        if (error.message === 'RATE_LIMIT_EXCEEDED' && (error as any).data) {
          setRateLimitData((error as any).data);
          setShowRateLimitModal(true);
          throw error;
        }
        throw error;
      }

      // Get the default logo from localStorage
      const defaultLogo = getDefaultLogo();
      logger.info('[useInvoiceGeneration] Default logo for generated invoice:', defaultLogo ? 'Found' : 'Not found');

      // Get the default sign/stamp from localStorage
      const defaultSignStamp = getDefaultSignStamp();
      logger.info('[useInvoiceGeneration] Default sign/stamp for generated invoice:', defaultSignStamp ? 'Found' : 'Not found');

      // Apply the default logo and sign/stamp to the generated invoice if they exist
      let invoiceWithDefaults: Invoice = { ...response.invoice };

      if (defaultLogo && !response.invoice.fromLogo) {
        invoiceWithDefaults = {
          ...invoiceWithDefaults,
          fromLogo: defaultLogo,
        };
        logger.info('[useInvoiceGeneration] Applied default logo to generated invoice');
      } else if (response.invoice.fromLogo) {
        logger.info('[useInvoiceGeneration] Generated invoice already has a logo');
      } else {
        logger.info('[useInvoiceGeneration] No logo to apply to generated invoice');
      }

      if (defaultSignStamp && !response.invoice.fromSignStamp) {
        invoiceWithDefaults = {
          ...invoiceWithDefaults,
          fromSignStamp: defaultSignStamp,
        };
        logger.info('[useInvoiceGeneration] Applied default sign/stamp to generated invoice');
      } else if (response.invoice.fromSignStamp) {
        logger.info('[useInvoiceGeneration] Generated invoice already has a sign/stamp');
      } else {
        logger.info('[useInvoiceGeneration] No sign/stamp to apply to generated invoice');
      }

      setCurrentInvoice(invoiceWithDefaults);
      addToHistory(invoiceWithDefaults);

      if (response.sessionId && !sessionId) {
        setSessionId(response.sessionId);
      }

      return invoiceWithDefaults;
    } catch (error: any) {
      if (error.message === 'RATE_LIMIT_EXCEEDED' && error.data) {
        setRateLimitData(error.data);
        setShowRateLimitModal(true);
      } else {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to generate invoice';
        setError(errorMessage);
      }
      throw error;
    } finally {
      setGenerating(false);
    }
  };

  const handleBonusAdded = () => {
    setRateLimitData(null);
    setShowRateLimitModal(false);
  };

  const handleCloseModal = () => {
    setShowRateLimitModal(false);
  };

  return {
    generateInvoice,
    isGenerating: useInvoiceStore((state) => state.isGenerating),
    error: useInvoiceStore((state) => state.error),
    rateLimitData,
    showRateLimitModal,
    handleBonusAdded,
    handleCloseModal,
  };
}
